import { Link } from "@/routing/link";
import Layout from "../components/Layout";
import { PageSEO } from "../../components/seo";
import dbLock from "../../assets/images/screen-db-lock.svg";
import mapMediumLight from "../../assets/images/map-medium.png";
import mapMediumDark from "../../assets/images/map-medium_dark.png";
import DataPrivacyHeadline from "../components/DataPrivacyHeadline";
import womenSmilingImage from "@/assets/photo/women-smiling.jpg";
import deFlag from "../../assets/icons/de-flag.svg";
import euFlag from "../../assets/icons/eu-flag.svg";
import { useTranslation } from "react-i18next";

// Hero Section
const HeroSection = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
    <div className="mx-auto max-w-screen-xl px-4 py-8 sm:py-16 lg:py-24">
      <div className="text-center">
        <h1 className="mb-6 font-headline text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-6xl">
          {t("dataPrivacy.hero.titleFirst")} <br />
          <span className="text-energetic-mint">{t("dataPrivacy.hero.titleSecond")}</span>
        </h1>
        <p className="mx-auto max-w-3xl text-lg text-graphite dark:text-gray-300 sm:text-xl">
          {t("dataPrivacy.hero.description")}
        </p>

        {/* Trust indicators */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 text-sm text-graphite dark:text-gray-400 sm:flex-row sm:gap-6">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 flex-shrink-0 text-energetic-mint"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="whitespace-nowrap">{t("dataPrivacy.hero.certified")}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 flex-shrink-0 text-energetic-mint"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="whitespace-nowrap">{t("dataPrivacy.hero.germanServers")}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 flex-shrink-0 text-energetic-mint"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="whitespace-nowrap">{t("dataPrivacy.hero.withoutCookies")}</span>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

// Updated Data Privacy Principles with CMO-friendly language
const DataPrivacyPrinciples = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-white dark:border-gray-700 dark:bg-gray-900">
    <div className="mx-auto max-w-screen-xl px-4 py-8 sm:py-16 lg:py-24">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div>
              <h2 className="text-3xl font-extrabold leading-tight text-gray-900 dark:text-white sm:text-4xl">
                {t("dataPrivacy.principles.title")}
              </h2>
              <p className="mt-4 text-base font-normal text-gray-500 dark:text-gray-400 sm:text-xl">
                {t("dataPrivacy.principles.description")}
              </p>
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-800 sm:pt-6 lg:pt-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                    <strong>{t("dataPrivacy.principles.relevantTitle")} </strong>
                    {t("dataPrivacy.principles.relevantDescription")}
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                    <strong>{t("dataPrivacy.principles.rulesTitle")} </strong>
                    {t("dataPrivacy.principles.rulesDescription")}
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                    <strong>{t("dataPrivacy.principles.transparencyTitle")} </strong>
                    {t("dataPrivacy.principles.transparencyDescription")}
                  </span>
                </li>

                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                    <strong>{t("dataPrivacy.principles.securityTitle")} </strong>
                    {t("dataPrivacy.principles.securityDescription")}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            <img
              className="h-64 w-full rounded-2xl object-cover shadow-2xl sm:h-80 lg:h-96"
              src={womenSmilingImage}
              alt={t("dataPrivacy.principles.imageAlt")}
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-energetic-mint/10 to-transparent"></div>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

// FAQ Section for CMO concerns
const FAQSection = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-gray-50 py-16 dark:bg-gray-800 lg:py-24">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <div className="mb-12 text-center">
        <h2 className="mb-4 text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
          {t("dataPrivacy.faq.title")}
        </h2>
        <p className="text-lg text-gray-500 dark:text-gray-400">
          {t("dataPrivacy.faq.description")}
        </p>
      </div>

      <div className="space-y-6">
        {/* FAQ Item 1 */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.faq.qualityQuestion")}
          </h3>
          <p className="text-graphite dark:text-gray-300">
            {t("dataPrivacy.faq.qualityAnswer")}
          </p>
        </div>

        {/* FAQ Item 2 */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.faq.retargetingQuestion")}
          </h3>
          <p className="text-graphite dark:text-gray-300">
            {t("dataPrivacy.faq.retargetingAnswer")}
          </p>
        </div>

        {/* FAQ Item 3 */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.faq.auditQuestion")}
          </h3>
          <p className="text-graphite dark:text-gray-300">
            {t("dataPrivacy.faq.auditAnswer")}
          </p>
        </div>

        {/* FAQ Item 4 */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.faq.speedQuestion")}
          </h3>
          <p className="text-graphite dark:text-gray-300">
            {t("dataPrivacy.faq.speedAnswer")}
          </p>
        </div>

        {/* FAQ Item 5 */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.faq.differenceQuestion")}
          </h3>
          <p className="text-graphite dark:text-gray-300">
            {t("dataPrivacy.faq.differenceAnswer")}
          </p>
        </div>
      </div>
    </div>
    </section>
  );
};

const HostingInGermany = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-white drop-shadow dark:border-gray-700 dark:bg-gray-900">
    <div className="mx-auto max-w-screen-xl px-4 py-8 sm:py-16 lg:px-6">
      <div className="mt-8 grid grid-cols-1 gap-8 lg:mt-20 lg:grid-cols-2 lg:gap-16">
        <div className="hidden lg:block">
          <img
            className="w-full object-cover dark:hidden"
            src={mapMediumLight}
            alt={t("dataPrivacy.hosting.mapAlt")}
          />
          <img
            className="hidden w-full object-cover dark:block"
            src={mapMediumDark}
            alt={t("dataPrivacy.hosting.mapAlt")}
          />
        </div>

        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <div>
            <h2 className="text-3xl font-extrabold leading-tight text-gray-900 dark:text-white sm:text-4xl">
              {t("dataPrivacy.hosting.title")}
            </h2>
            <p className="mt-4 text-base font-normal text-gray-500 dark:text-gray-400 sm:text-xl">
              {t("dataPrivacy.hosting.description")}
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4 dark:border-gray-800 sm:pt-6 lg:pt-8">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                  <strong>{t("dataPrivacy.hosting.complianceTitle")} </strong>
                  {t("dataPrivacy.hosting.complianceDescription")}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                  <strong>{t("dataPrivacy.hosting.technologyTitle")} </strong>
                  {t("dataPrivacy.hosting.technologyDescription")}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                  <strong>{t("dataPrivacy.hosting.energyTitle")} </strong>
                  {t("dataPrivacy.hosting.energyDescription")}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                  <strong>{t("dataPrivacy.hosting.transparencyTitle")} </strong>
                  {t("dataPrivacy.hosting.transparencyDescription")}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-energetic-mint"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-base leading-relaxed text-gray-900 dark:text-white">
                  <strong>{t("dataPrivacy.hosting.transferTitle")} </strong>
                  {t("dataPrivacy.hosting.transferDescription")}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

// Digital Sovereignty Teaser Section
const DigitaleSouveraenitaetTeaser = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 lg:py-24">
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-8 lg:flex-row lg:gap-16">
        <div className="flex-1 text-center lg:text-left">
          <span className="mb-4 inline-block rounded-full bg-energetic-mint/20 px-4 py-1.5 text-sm font-semibold text-energetic-mint">
            {t("dataPrivacy.sovereignty.label")}
          </span>
          <h2 className="mb-4 text-3xl font-extrabold text-white sm:text-4xl">
            {t("dataPrivacy.sovereignty.title")}
          </h2>
          <p className="mb-6 text-lg text-gray-300">
            {t("dataPrivacy.sovereignty.description")}
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start">
            <Link
              to="/digitale-souveraenitaet"
              className="btn inline-flex items-center justify-center gap-2"
            >
              {t("dataPrivacy.sovereignty.learnMore")}
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        </div>
        <div className="flex-shrink-0">
          <div className="inline-flex flex-col items-center rounded-2xl bg-white/10 p-6 backdrop-blur">
            <div className="mb-4 flex items-center gap-4">
              <div className="flex flex-col items-center">
                <div className="overflow-hidden rounded-lg shadow-lg ring-2 ring-white">
                  <img src={deFlag} alt={t("dataPrivacy.sovereignty.germanyAlt")} className="h-16 w-24 object-cover" />
                </div>
                <span className="mt-2 text-xs font-bold text-white">{t("dataPrivacy.sovereignty.hosted")}</span>
              </div>
              <div className="flex flex-col items-center opacity-60">
                <div className="overflow-hidden rounded-lg shadow">
                  <img src={euFlag} alt={t("dataPrivacy.sovereignty.europeAlt")} className="h-12 w-18 object-cover" />
                </div>
                <span className="mt-2 text-xs text-gray-400">{t("dataPrivacy.sovereignty.compliant")}</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-300">
              {t("dataPrivacy.sovereignty.tagline")}
            </p>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

const TechnicalImplementation = () => {
  const { t } = useTranslation("website-trust");

  return (
    <section className="bg-white dark:border-gray-700 dark:bg-gray-900">
    <div className="mx-auto max-w-screen-xl px-4 py-8 text-center sm:py-16 lg:px-6">
      <h2 className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
        {t("dataPrivacy.technical.title")}
      </h2>
      <p className="text-gray-500 dark:text-gray-400 sm:text-xl lg:px-48">
        {t("dataPrivacy.technical.description")}
      </p>
      <div className="mb-8 mt-8 space-y-8 md:grid md:grid-cols-2 md:gap-12 md:space-y-0 lg:mt-16">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-energetic-mint/10">
            <svg
              className="h-8 w-8 text-energetic-mint"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.technical.cookielessTitle")}
          </h3>
          <p className="mb-4 text-graphite dark:text-gray-300">
            {t("dataPrivacy.technical.cookielessDescription")}
          </p>
        </div>
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-energetic-mint/10">
            <svg
              className="h-8 w-8 text-energetic-mint"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>
          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.technical.optOutTitle")}
          </h3>
          <p className="mb-4 text-graphite dark:text-gray-300">
            {t("dataPrivacy.technical.optOutBefore")} {" "}
            <Link
              to="/opt-out"
              className="text-energetic-mint underline hover:text-energetic-mint/80"
            >
              Opt-Out
            </Link>{" "}
            {" "}{t("dataPrivacy.technical.optOutAfter")}
          </p>
        </div>
      </div>

      <div className="mb-8 mt-8 space-y-8 md:grid md:grid-cols-2 md:gap-12 md:space-y-0 lg:mt-16">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-energetic-mint/10">
            <svg
              className="h-8 w-8 text-energetic-mint"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.technical.processingTitle")}
          </h3>
          <p className="mb-4 text-graphite dark:text-gray-300">
            {t("dataPrivacy.technical.processingDescription")}
          </p>
        </div>
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-energetic-mint/10">
            <svg
              className="h-8 w-8 text-energetic-mint"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
            {t("dataPrivacy.technical.storageTitle")}
          </h3>
          <p className="mb-4 text-graphite dark:text-gray-300">
            {t("dataPrivacy.technical.storageDescription")}
          </p>
        </div>
      </div>
    </div>
    </section>
  );
};

const DataPrivacyPage = () => {
  const { t } = useTranslation("website-trust");

  return (
    <Layout>
      <PageSEO
        title={t("dataPrivacy.seo.title")}
        description={t("dataPrivacy.seo.description")}
        keywords={t("dataPrivacy.seo.keywords", { returnObjects: true }) as string[]}
        path="/datenschutz"
        openGraph={{
          title: t("dataPrivacy.seo.socialTitle"),
          description: t("dataPrivacy.seo.socialDescription"),
        }}
        twitter={{
          title: t("dataPrivacy.seo.socialTitle"),
          description: t("dataPrivacy.seo.twitterDescription"),
        }}
      />
      <HeroSection />
      <DataPrivacyPrinciples />
      <DataPrivacyHeadline />
      <HostingInGermany />
      <DigitaleSouveraenitaetTeaser />
      <TechnicalImplementation />
      <FAQSection />
    </Layout>
  );
};

export default DataPrivacyPage;
